import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextB, TextItalic, TextUnderline, TextStrikethrough, LinkSimple, ListBullets, ListNumbers, TextAa, Highlighter } from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Shared styles (lists, links, placeholder) — Tailwind's preflight resets list
// markers, so rich-text bullets/numbers need explicit CSS. Injected once.
// ---------------------------------------------------------------------------
const STYLE_ID = 'richtext-styles';
const STYLES = `
.richtext-content ul { list-style: disc; padding-left: 1.5rem; margin: 0.25rem 0; }
.richtext-content ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
.richtext-content li { margin: 0.125rem 0; }
.richtext-content p { margin: 0.25rem 0; }
/* Tailwind Preflight flattens headings to inherit — re-assert sizes here. */
.richtext-content h2 { font-size: 1.375rem; font-weight: 700; line-height: 1.3; margin: 0.5rem 0 0.25rem; }
.richtext-content h3 { font-size: 1.0625rem; font-weight: 600; line-height: 1.35; margin: 0.4rem 0 0.2rem; }
.richtext-content a { color: var(--primary); text-decoration: underline; }
/* Preflight's "font-weight: bolder" is relative and goes flat in light-weight
   contexts (e.g. the apply hero) — pin absolute weight/style so b/i/u always show. */
.richtext-content b, .richtext-content strong { font-weight: 700 !important; }
.richtext-content i, .richtext-content em { font-style: italic !important; }
.richtext-content u { text-decoration: underline; }
.richtext-content s, .richtext-content strike, .richtext-content del { text-decoration: line-through; }
/* Legacy descriptions used inline-styled spans for bold/italic — keep them too. */
.richtext-content span[style*="font-weight"] { font-weight: 700 !important; }
.richtext-editor:empty:before { content: attr(data-placeholder); color: var(--text-muted); pointer-events: none; }
.richtext-editor:focus { outline: none; }
`;

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// Sanitizer — the security boundary for displaying stored HTML. Whitelist of
// tags + a tiny set of inline style props. Anything else is unwrapped/stripped.
// ---------------------------------------------------------------------------
const ALLOWED_TAGS = new Set(['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'UL', 'OL', 'LI', 'SPAN', 'DIV', 'A', 'H1', 'H2', 'H3']);
const ALLOWED_STYLE = /^(font-size|font-weight|font-style|text-decoration|color|background-color)$/;

function sanitizeHtml(html) {
  if (typeof window === 'undefined' || !window.DOMParser) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const clean = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === 3) return;            // text node — keep
      if (child.nodeType !== 1) { child.remove(); return; } // comment etc — drop
      clean(child);                                // clean subtree first
      const tag = child.tagName;
      if (!ALLOWED_TAGS.has(tag)) {                // unwrap unknown tags, keep text
        const parent = child.parentNode;
        while (child.firstChild) parent.insertBefore(child.firstChild, child);
        parent.removeChild(child);
        return;
      }
      Array.from(child.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name === 'style') {
          const safe = child.style.cssText.split(';').map((s) => s.trim()).filter(Boolean)
            .filter((decl) => ALLOWED_STYLE.test(decl.split(':')[0].trim().toLowerCase()))
            .join('; ');
          if (safe) child.setAttribute('style', safe); else child.removeAttribute('style');
        } else if (tag === 'A' && name === 'href') {
          const href = attr.value.trim();
          if (/^(https?:|mailto:)/i.test(href)) {
            child.setAttribute('rel', 'noreferrer');
            child.setAttribute('target', '_blank');
          } else {
            child.removeAttribute('href');
          }
        } else {
          child.removeAttribute(attr.name);
        }
      });
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
}

const looksLikeHtml = (s) => /<[a-z][\s\S]*>/i.test(s);
const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// Convert a stored description into safe display HTML. Legacy plain-text
// descriptions (no tags) are escaped and newlines turned into <br>.
function toDisplayHtml(value) {
  if (!value) return '';
  if (!looksLikeHtml(value)) return escapeHtml(value).replace(/\n/g, '<br>');
  return sanitizeHtml(value);
}

// ---------------------------------------------------------------------------
// RichText — render a stored (possibly rich) description safely.
// ---------------------------------------------------------------------------
export function RichText({ html, className = '' }) {
  useEffect(injectStyles, []);
  const clean = useMemo(() => toDisplayHtml(html), [html]);
  if (!clean) return null;
  return <div className={`richtext-content ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />;
}

// Plain-text extraction from (possibly rich) HTML — used for emptiness checks,
// toast messages, and anywhere a label must render as text, not markup.
export const stripHtml = (html) => {
  if (!html) return '';
  if (typeof window === 'undefined' || !window.DOMParser) return String(html).replace(/<[^>]*>/g, '');
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return (doc.body.textContent || '').trim();
};

// ---------------------------------------------------------------------------
// LinkPrompt — shared popup for inserting a hyperlink. Replaces the native
// window.prompt so every rich-text field in the program form (description,
// question labels, section titles) uses one consistent link dialog with
// placeholder text. Renders nothing when closed.
// ---------------------------------------------------------------------------
function LinkPrompt({ open, initialText = '', onSubmit, onClose }) {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const textRef = useRef(null);
  const urlRef = useRef(null);
  useEffect(() => {
    if (open) {
      setText(initialText);
      setUrl('');
      // Jump straight to the URL field when the display text is already known
      // (from a selection); otherwise start at the text field.
      setTimeout(() => { (initialText ? urlRef.current : textRef.current)?.focus(); }, 0);
    }
  }, [open, initialText]);
  if (!open) return null;
  const u = url.trim();
  const t = text.trim();
  // Display text falls back to the URL if left blank.
  const submit = () => { if (u) onSubmit({ text: t || u, url: u }); };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };
  const inputCls = 'w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary';
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={onClose} data-testid="link-prompt">
      <div className="bg-surface border border-border rounded-xl w-full max-w-sm p-5 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold text-text-primary mb-1">Add link</div>
        <p className="text-[11px] text-text-secondary mb-3">Links open in a new tab.</p>
        <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5">Text to display</label>
        <input
          ref={textRef}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="e.g. Apply here"
          className={inputCls}
          data-testid="link-prompt-text"
        />
        <label className="block text-[11px] uppercase tracking-wider text-text-secondary mb-1.5 mt-3">URL</label>
        <input
          ref={urlRef}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="https://example.com"
          className={inputCls}
          data-testid="link-prompt-url"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose}
            className="px-3 py-2 text-sm font-medium border border-border rounded-lg text-text-secondary hover:text-text-primary"
            data-testid="link-prompt-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={!u}
            className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg disabled:opacity-50"
            data-testid="link-prompt-submit">Add link</button>
        </div>
      </div>
    </div>
  );
}

// Snapshot the current selection if it lives inside `el` — opening the popup
// (focusing its input) collapses the live selection, so we save it first.
function snapshotSelection(el) {
  const sel = window.getSelection();
  if (sel && sel.rangeCount && el?.contains(sel.anchorNode)) return sel.getRangeAt(0).cloneRange();
  return null;
}

// Restore a saved selection into `el` and replace it with an <a> using the
// display text + URL from the popup. Shared by both editors so the hyperlink
// behaviour is identical across the program form.
function applyLink(el, savedRange, text, url) {
  el?.focus();
  const sel = window.getSelection();
  if (savedRange && sel) { sel.removeAllRanges(); sel.addRange(savedRange); }
  const label = (text || url).trim();
  document.execCommand('insertHTML', false, `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`);
}

// ---------------------------------------------------------------------------
// InlineRichEditor — compact single-field rich editor for short labels
// (question text, section titles). Toolbar: Bold / Italic / Strikethrough / Link.
// Value is sanitized HTML; onChange fires with current HTML on every edit.
// ---------------------------------------------------------------------------
export function InlineRichEditor({ value, onChange, placeholder = '', className = '', 'data-testid': testId }) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  useEffect(injectStyles, []);

  // Sync external value in (dup / block-add / reorder) without stomping the caret
  // while the user is actively typing in this field.
  useEffect(() => {
    const el = ref.current;
    if (!el || document.activeElement === el) return;
    const incoming = value || '';
    if (el.innerHTML !== incoming) el.innerHTML = incoming;
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || '');
  const exec = (cmd) => { ref.current?.focus(); document.execCommand(cmd, false); emit(); };
  const openLink = () => { savedRange.current = snapshotSelection(ref.current); setLinkText(savedRange.current?.toString() || ''); setLinkOpen(true); };
  const submitLink = ({ text, url }) => { setLinkOpen(false); applyLink(ref.current, savedRange.current, text, url); emit(); };

  const tools = [
    { icon: TextB, cmd: 'bold', title: 'Bold' },
    { icon: TextItalic, cmd: 'italic', title: 'Italic' },
    { icon: TextStrikethrough, cmd: 'strikeThrough', title: 'Strikethrough' },
  ];

  return (
    <div className={className}>
      {/* Clean underlined input (no grey fill) — highlights on focus, Google-Forms style */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        data-placeholder={placeholder}
        onInput={emit}
        className="richtext-content richtext-editor border-b-2 border-border focus:border-primary px-0.5 py-2 text-[15px] text-text-primary transition-colors"
        data-testid={testId}
      />
      {/* Formatting toolbar below the field */}
      <div className="flex items-center gap-0.5 pt-1.5">
        {tools.map((t) => (
          <button key={t.cmd} type="button" title={t.title}
            onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
            className="w-7 h-7 flex items-center justify-center rounded text-text-secondary hover:text-primary hover:bg-primary/10 transition-colors"
            data-testid={testId ? `${testId}-${t.cmd}` : undefined}>
            <t.icon size={15} weight="bold" />
          </button>
        ))}
        <button type="button" title="Add link"
          onMouseDown={(e) => { e.preventDefault(); openLink(); }}
          className="w-7 h-7 flex items-center justify-center rounded text-text-secondary hover:text-primary hover:bg-primary/10 transition-colors"
          data-testid={testId ? `${testId}-link` : undefined}>
          <LinkSimple size={15} weight="bold" />
        </button>
      </div>
      <LinkPrompt open={linkOpen} initialText={linkText} onSubmit={submitLink} onClose={() => setLinkOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RichTextEditor — Teams-style toolbar over a contentEditable surface.
// Value is HTML; onChange fires with the current HTML on every edit.
// ---------------------------------------------------------------------------
// Block-level "sizes" — emit semantic tags so rendering survives Tailwind's
// Preflight (numeric font-size spans were getting flattened on display).
const TEXT_SIZES = [
  { label: 'Text', block: 'P' },
  { label: 'Subheader', block: 'H3' },
  { label: 'Header', block: 'H2' },
];

export function RichTextEditor({ value, onChange, placeholder = '', className = '', 'data-testid': testId }) {
  const ref = useRef(null);

  useEffect(injectStyles, []);

  // Seed initial content once; we keep the field uncontrolled afterwards so the
  // caret never jumps mid-typing.
  useEffect(() => {
    if (ref.current && value && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || '');

  const exec = (command, arg) => {
    ref.current?.focus();
    // No styleWithCSS: bold/italic/underline emit semantic <b>/<i>/<u> tags,
    // which render reliably (Chrome's CSS-span output was being lost on display).
    document.execCommand(command, false, arg);
    emit();
  };

  // The native color picker blurs the editor and collapses the selection, so we
  // snapshot the range on mousedown and restore it before applying the color.
  const savedRange = useRef(null);
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const applyColor = (command, value) => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    // styleWithCSS so foreColor/hiliteColor emit <span style> not legacy <font>.
    document.execCommand('styleWithCSS', false, true);
    document.execCommand(command, false, value);
    document.execCommand('styleWithCSS', false, false);
    emit();
  };

  // Wrap the current selection in an <a> via the shared LinkPrompt popup. The
  // sanitizer whitelists http(s)/mailto hrefs (and adds target/rel) on save +
  // display. Snapshot the selection first — opening the popup collapses it.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const openLink = () => { saveSelection(); setLinkText(savedRange.current?.toString() || ''); setLinkOpen(true); };
  const submitLink = ({ text, url }) => { setLinkOpen(false); applyLink(ref.current, savedRange.current, text, url); emit(); };

  const tools = [
    { icon: TextB, cmd: 'bold', title: 'Bold' },
    { icon: TextItalic, cmd: 'italic', title: 'Italic' },
    { icon: TextUnderline, cmd: 'underline', title: 'Underline' },
    { icon: ListBullets, cmd: 'insertUnorderedList', title: 'Bulleted list' },
    { icon: ListNumbers, cmd: 'insertOrderedList', title: 'Numbered list' },
  ];

  return (
    <div className={`bg-background border border-border rounded-lg focus-within:border-primary overflow-hidden ${className}`}>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-border bg-black flex-wrap">
        {tools.map((t) => (
          <button
            key={t.cmd}
            type="button"
            title={t.title}
            // onMouseDown + preventDefault keeps the text selection while clicking the button
            onMouseDown={(e) => { e.preventDefault(); exec(t.cmd); }}
            className="p-1.5 rounded text-white hover:bg-white/15 transition-colors"
            data-testid={`richtext-${t.cmd}`}
          >
            <t.icon size={16} weight="bold" />
          </button>
        ))}
        <span className="w-px h-4 bg-white/25 mx-1" />
        <button
          type="button"
          title="Add link"
          onMouseDown={(e) => { e.preventDefault(); openLink(); }}
          className="p-1.5 rounded text-white hover:bg-white/15 transition-colors"
          data-testid="richtext-link"
        >
          <LinkSimple size={16} weight="bold" />
        </button>
        <span className="w-px h-4 bg-white/25 mx-1" />
        <label title="Text color" onMouseDown={saveSelection}
          className="relative p-1.5 rounded text-white hover:bg-white/15 transition-colors cursor-pointer flex items-center">
          <TextAa size={16} weight="bold" />
          <input type="color" defaultValue="#6c63ff"
            onMouseDown={saveSelection}
            onChange={(e) => applyColor('foreColor', e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="richtext-forecolor" />
        </label>
        <label title="Highlight" onMouseDown={saveSelection}
          className="relative p-1.5 rounded text-white hover:bg-white/15 transition-colors cursor-pointer flex items-center">
          <Highlighter size={16} weight="bold" />
          <input type="color" defaultValue="#ffe066"
            onMouseDown={saveSelection}
            onChange={(e) => applyColor('hiliteColor', e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            data-testid="richtext-hilitecolor" />
        </label>
        <span className="w-px h-4 bg-white/25 mx-1" />
        <select
          title="Text size"
          defaultValue=""
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { const v = e.target.value; if (v) exec('formatBlock', v); e.target.value = ''; }}
          className="bg-black text-white font-bold border border-white/25 rounded px-1.5 py-1 text-xs focus:outline-none focus:border-white cursor-pointer"
          data-testid="richtext-fontsize"
        >
          <option value="" disabled>Size</option>
          {TEXT_SIZES.map((f) => <option key={f.block} value={f.block}>{f.label}</option>)}
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={emit}
        className="richtext-content richtext-editor min-h-[88px] max-h-[240px] overflow-y-auto px-3 py-2.5 text-sm text-text-primary leading-relaxed"
        data-testid={testId}
      />
      <LinkPrompt open={linkOpen} initialText={linkText} onSubmit={submitLink} onClose={() => setLinkOpen(false)} />
    </div>
  );
}

export default RichTextEditor;
